import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, X, ChevronLeft, ChevronRight, Menu, 
  Image, Video, Camera, LogOut, Heart, Download
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Masonry from 'react-masonry-css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InstaweddingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [guestPhotos, setGuestPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [zipDownloading, setZipDownloading] = useState(false);
  const isSposi = user?.role === 'sposi';

  const handleDownloadAllZip = async () => {
    if (zipDownloading || !gallery?.id) return;
    setZipDownloading(true);
    try {
      const res = await axios.get(`${API}/galleries/${gallery.id}/guest-photos/download-zip`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${gallery.title?.replace(/ /g, '_') || 'evento'}_instawedding.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('ZIP download error:', error);
      alert('Errore durante il download del file ZIP');
    } finally {
      setZipDownloading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get gallery info for this client
      const galleryRes = await axios.get(`${API}/gallery`);
      setGallery({
        id: galleryRes.data.id,
        title: galleryRes.data.title,
        event_date: galleryRes.data.event_date
      });
      
      // Get guest photos from public endpoint
      const eventRes = await axios.get(`${API}/public/event/${galleryRes.data.id}`);
      setGuestPhotos(eventRes.data.photos || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setLightboxIndex((prev) => (prev === 0 ? guestPhotos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setLightboxIndex((prev) => (prev === guestPhotos.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') closeLightbox();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, guestPhotos.length]);

  // Masonry breakpoints
  const breakpointCols = {
    default: 5,
    1280: 4,
    1024: 3,
    768: 2,
    640: 2
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <span className="font-medium text-gray-800">Menu</span>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <nav className="space-y-2">
                <button
                  onClick={() => { navigate('/gallery'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Image className="w-5 h-5" />
                  Tutte le foto
                </button>
                <button
                  onClick={() => { navigate('/videos'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Video className="w-5 h-5" />
                  Tutti i video
                </button>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Instawedding
                </button>
                <button
                  onClick={() => { navigate('/photobooth'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Foto Boot
                </button>
                <button
                  onClick={() => { handleLogout(); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Esci
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-start justify-between">
            {/* Menu icon */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Center: Title */}
            <div className="text-center flex-1">
              <h1 className="font-wedding text-4xl text-gray-800">{gallery?.title}</h1>
              {gallery?.event_date && (
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(gallery.event_date).toLocaleDateString('it-IT', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              )}
            </div>
            
            {/* Spacer */}
            <div className="w-10" />
          </div>
        </div>
        
        {/* Section title */}
        <div className="bg-gradient-to-r from-pink-500 to-purple-600 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 flex-wrap">
            <Camera className="w-5 h-5 text-white" />
            <h2 className="text-white font-medium">Instawedding</h2>
            <span className="text-white/80 text-sm">• {guestPhotos.length} foto degli ospiti</span>
            {isSposi && guestPhotos.length > 0 && (
              <button
                onClick={handleDownloadAllZip}
                disabled={zipDownloading}
                data-testid="instawedding-download-zip-btn"
                className="ml-2 flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
              >
                {zipDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {zipDownloading ? 'Preparazione...' : 'Scarica tutto (ZIP)'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {guestPhotos.length === 0 ? (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">Nessuna foto degli ospiti</p>
            <p className="text-gray-400 text-sm">Le foto caricate dagli ospiti appariranno qui</p>
          </div>
        ) : (
          <Masonry
            breakpointCols={breakpointCols}
            className="masonry-grid"
            columnClassName="masonry-grid-column"
          >
            {guestPhotos.map((photo, index) => (
              <PhotoCard 
                key={photo.id}
                photo={photo}
                onClick={() => openLightbox(index)}
              />
            ))}
          </Masonry>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && guestPhotos.length > 0 && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            onClick={goToPrevious}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          {guestPhotos[lightboxIndex]?.media_type === 'video' ? (
            <video
              src={guestPhotos[lightboxIndex]?.url}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              controls
              playsInline
              autoPlay
            />
          ) : (
            <img
              src={guestPhotos[lightboxIndex]?.url}
              alt=""
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          )}
          
          <button
            onClick={goToNext}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          {/* Photo info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center">
            <div className="bg-black/50 px-4 py-2 rounded-full mb-2">
              {lightboxIndex + 1} / {guestPhotos.length}
            </div>
            {guestPhotos[lightboxIndex]?.guest_name && (
              <div className="bg-black/50 px-4 py-2 rounded-full text-sm">
                📷 {guestPhotos[lightboxIndex].guest_name}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Photo Card Component
const PhotoCard = ({ photo, onClick }) => {
  const isVideo = photo.media_type === 'video';
  
  if (isVideo) {
    return (
      <div className="relative rounded-xl overflow-hidden bg-black mb-4">
        <video 
          src={photo.url}
          className="w-full h-auto block"
          controls
          playsInline
          preload="metadata"
        />
        {photo.guest_name && (
          <div className="absolute top-2 left-2 pointer-events-none">
            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              📷 {photo.guest_name}
            </span>
          </div>
        )}
        {photo.likes_count > 0 && (
          <div className="absolute bottom-2 right-2 pointer-events-none">
            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Heart className="w-3 h-3 fill-red-500 text-red-500" />
              {photo.likes_count}
            </span>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div 
      className="group relative rounded-xl overflow-hidden bg-gray-100 cursor-pointer mb-4"
      onClick={onClick}
    >
      <img 
        src={photo.thumbnail_url || photo.url}
        alt=""
        className="w-full h-auto block"
        loading="lazy"
      />
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors">
        {/* Guest name badge */}
        {photo.guest_name && (
          <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              📷 {photo.guest_name}
            </span>
          </div>
        )}
      </div>
      
      {/* Likes badge */}
      {photo.likes_count > 0 && (
        <div className="absolute bottom-2 right-2 pointer-events-none">
          <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Heart className="w-3 h-3 fill-red-500 text-red-500" />
            {photo.likes_count}
          </span>
        </div>
      )}
    </div>
  );
};

export default InstaweddingPage;
